import { isValid, parse, format, isAfter } from "date-fns"

import { useTranslationsStore } from "@/stores/translations.js"

import * as quasarUtils from "@/services/quasarUtils.js"

const apiFormat = "yyyy-MM-dd"
const apiFormatRegex = new RegExp("^\\d\\d\\d\\d-\\d\\d-\\d\\d$")

const getDateFormatForLanguage = (lang) => {
  switch (lang) {
    case "fr":
      return "dd-MM-yyyy"
    default:
      return "yyyy-MM-dd"
  }
}

const getDateRegexForLanguage = (lang) => {
  switch (lang) {
    case "fr":
      return new RegExp("^\\d\\d-\\d\\d-\\d\\d\\d\\d$")
    default:
      return new RegExp("^\\d\\d\\d\\d-\\d\\d-\\d\\d$")
  }
}

const getDateFormatForCurrentLanguage = () => {
  const translationsStore = useTranslationsStore()
  return getDateFormatForLanguage(translationsStore.currentLanguage)
}

const getLocalizedStringFromDate = (value) => {
  if (!value) {
    return ""
  }

  try {
    const outputFormat = getDateFormatForCurrentLanguage()
    return format(new Date(value), outputFormat)
  } catch (err) {
    quasarUtils.handleError(err)
    return ""
  }
}

export const parseInCurrentFormat = (value) => parse(value, getDateFormatForCurrentLanguage(), new Date())

export const getApiStringFromDateString = (value) => {
  const date = parseInCurrentFormat(value)
  return getApiStringFromDate(date)
}

export const convertDateStringToUniversalMiliseconds = (dateString) => {
  const date = isDateStringValid(dateString, apiFormat) ? new Date(dateString) : undefined
  if (date) {
    return date.getTime()
  }
  return 0
}

export const isDateStringValid = (dateString, dateFormat) => {
  if (!dateString) {
    return false
  }

  const regex = dateFormat === apiFormat ? apiFormatRegex : getDateRegexForLanguage(useTranslationsStore().currentLanguage)
  if (!regex.test(dateString)) {
    return false
  }

  const parsed = parse(dateString, dateFormat || getDateFormatForCurrentLanguage(), new Date())

  return isValid(parsed)
}

export const getApiStringFromDate = (date) => {
  if (!date) {
    return ""
  }
  try {
    return format(date, apiFormat)
  } catch (err) {
    quasarUtils.handleError(err)
    return ""
  }
}

export const getTimeStringFromDate = (date) => {
  if (!date) {
    return ""
  }
  try {
    return format(date, "HH:mm")
  } catch (err) {
    quasarUtils.handleError(err)
    return ""
  }
}

export const getLocalizedStringFromDateTime = (value, skipTime = false) => {
  if (!value) {
    return ""
  }
  const splitted = value.split(" ")

  let date = null
  if (isDateStringValid(splitted[0], apiFormat)) {
    date = parse(splitted[0], apiFormat, new Date())
  }

  if (!date) {
    return ""
  }

  let ret = getLocalizedStringFromDate(date)

  if (splitted.length > 1 && !skipTime) {
    ret += " " + splitted[1]
  }
  return ret
}

export const isAfterOrEqual = (date1, date2) => isAfter(date1, date2) || date1.toISOString().split("T")[0] === date2.toISOString().split("T")[0]

// Backend stores UTC datetimes as "yyyy-MM-dd HH:mm:ss" without timezone suffix.
// Appending "Z" tells JavaScript's Date parser to interpret the string as UTC,
// so the resulting Date object correctly represents the UTC instant in local time.
export const parseUtcString = (utcString) => (utcString ? new Date(`${utcString}Z`) : null)

// Splits a UTC datetime string into local date (API format) and local time (HH:mm) parts.
// Useful for populating separate date picker and time picker inputs from a single UTC value.
export const utcStringToLocalParts = (utcString) => {
  if (!utcString) {
    return { date: null, time: "" }
  }
  const local = parseUtcString(utcString)
  return {
    date: getApiStringFromDate(local),
    time: getTimeStringFromDate(local)
  }
}

// Combines local date (yyyy-MM-dd) and time (HH:mm) into a UTC datetime string ("yyyy-MM-dd HH:mm:ss").
// Constructs a local Date from the parts, then extracts UTC components to build the output string
// in the format expected by the backend's JsonDateTimeConverter.
export const localPartsToUtcString = (date, time) => {
  if (!date) {
    return null
  }
  const timeStr = time || "00:00"
  const local = new Date(`${date}T${timeStr}:00`)
  const yyyy = local.getUTCFullYear()
  const mm = String(local.getUTCMonth() + 1).padStart(2, "0")
  const dd = String(local.getUTCDate()).padStart(2, "0")
  const hh = String(local.getUTCHours()).padStart(2, "0")
  const min = String(local.getUTCMinutes()).padStart(2, "0")
  const ss = String(local.getUTCSeconds()).padStart(2, "0")
  return `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}`
}
